import { assign, chain, find, findIndex, get, indexOf, merge } from 'lodash';

import {
    TrackingParametersEditorActions,
    TrackingParametersEditorActionType
} from '../actions/trackingParametersEditor';
import { ErrorItem } from '../components/ErrorsPresenter/ErrorsPresenter';
import { UrlHelpers } from '../service/UrlHelpers';
import { State } from '../typings/state';
import {
    AppTracker,
    AppTrackerParams,
    JavaAppTrackerTemplate,
    MOBILE_APP_ID,
    TrackingParametersEditorsState,
    TrackingParametersEditorState
} from '../typings/trackingParametersEditor';

const trackerIdPlaceholder = '{tracker_id}';

/**
 * Формируем трекинговую ссылку из базовой ссылки
 * и выбранных параметров
 * @param osType
 * @param template
 * @param tracker
 */
function trackUrlBuild(tracker: AppTracker, template?: JavaAppTrackerTemplate, osType?: string): AppTracker {
    const { url, baseUrl, trackerId, selectedParams, selectedUserParams } = tracker;
    let newUrl = url;
    let newBaseUrl = baseUrl;
    let newBaseUrlWithId = baseUrl;

    if (baseUrl && UrlHelpers.isUrlValid(baseUrl)) {
        const urlObj = new URL(baseUrl);
        const baseUrlObj = new URL(baseUrl);
        const searchValue = trackerIdPlaceholder;

        if (template && template.parameters) {
            // Удаляем старые обязательные параметры
            template.parameters.forEach(param => {
                if (param.required && param.osType.toLowerCase() !== osType) {
                    urlObj.searchParams.delete(param.name);
                    baseUrlObj.searchParams.delete(param.name);
                }
            });

            // Добавляем новые обязательные параметры
            template.parameters.forEach(param => {
                if (param.required && param.osType.toLowerCase() === osType) {
                    urlObj.searchParams.set(param.name, param.value);
                    baseUrlObj.searchParams.set(param.name, param.value);
                }
            });
        }

        (selectedParams || []).forEach(param => {
            urlObj.searchParams.set(param.name, param.value);
        });

        (selectedUserParams || []).forEach(param => {
            urlObj.searchParams.set(param.name, param.value);
        });

        // softURIDecode - чтобы не терялись спец. символы
        // Если url уже декодирова - возвращаем его же
        newUrl = softURIDecode(urlObj.href);
        newBaseUrl = softURIDecode(baseUrlObj.href);

        // обновляем url и базовую ссылку, если в них есть место для trackerId
        if (newBaseUrl.indexOf(searchValue) !== -1) {
            newBaseUrlWithId = newBaseUrl.replace(searchValue, trackerId || searchValue);
        }
        if (newUrl.indexOf(searchValue) !== -1) {
            newUrl = newUrl.replace(searchValue, trackerId || searchValue);
        }
    }

    return {
        url: newUrl,
        baseUrl: newBaseUrl,
        baseUrlWithId: newBaseUrlWithId
    };
}

/**
 * Разбираем ссылку на базовую часть, включающую обязательные параметры
 * и выбранные параметры (пользовательские и опциональные)
 * @param tracker
 * @param template
 */
function trackingUrlParse(tracker: AppTracker, template?: JavaAppTrackerTemplate): AppTracker {
    const { url, trackerId, userParams } = tracker;

    if (template && url && UrlHelpers.isUrlValid(url)) {
        const { parameters } = template;

        const baseUrlObj = new URL(url);
        const searchParams = baseUrlObj.searchParams;
        const selectedParams = [] as AppTrackerParams;
        const selectedUserParams = [] as AppTrackerParams;

        let baseUrl: string;
        let baseUrlWithId: string;

        searchParams.forEach((paramValue: string, paramName: string) => {
            const selectedParam = find(parameters, {
                name: paramName,
                required: false
            });
            const selectedUserParam = indexOf(userParams, paramName) !== -1;

            if (selectedParam) {
                selectedParams.push({
                    name: paramName,
                    value: paramValue
                });
            } else if (selectedUserParam) {
                selectedUserParams.push({
                    name: paramName,
                    value: paramValue
                });
            }
        });

        selectedParams.forEach(selParam => {
            searchParams.delete(selParam.name);
        });

        selectedUserParams.forEach(userParam => {
            searchParams.delete(userParam.name);
        });

        baseUrl = decodeURI(baseUrlObj.href);
        baseUrlWithId = decodeURI(baseUrlObj.href);

        if (trackerId && baseUrl.indexOf(trackerId) !== -1) {
            baseUrl = baseUrl.replace(trackerId, trackerIdPlaceholder);
        }

        return {
            baseUrl: baseUrl,
            baseUrlWithId: baseUrlWithId,
            selectedParams: selectedParams,
            selectedUserParams: selectedUserParams,
            urlEditable: template.editable
        };
    } else {
        return {
            baseUrl: url,
            selectedUserParams: [],
            selectedParams: []
        };
    }
}

function softURIDecode(url: string): string {
    try {
        return decodeURI(url);
    } catch (err) {
        return url;
    }
}

const trackingParametersInitialState: TrackingParametersEditorsState = {
    data: {
        [MOBILE_APP_ID]: {
            tracker: {}
        }
    },
    trackersTemplates: []
};

export function trackingParametersEditor(
    state: TrackingParametersEditorsState = trackingParametersInitialState,
    action: TrackingParametersEditorActions,
    fullState: State
): TrackingParametersEditorsState {
    switch (action.type) {
        case TrackingParametersEditorActionType.UPDATE_APP_TRACKERS_APP_INFO: {
            const editor = state.data[action.editorId];

            return {
                ...state,
                data: {
                    ...state.data,
                    [action.editorId]: {
                        ...editor,
                        tracker: action.appInfo
                            ? {
                                  ...editor.tracker,
                                  ...trackUrlBuild(
                                      editor.tracker,
                                      find(state.trackersTemplates, {
                                          trackingSystem: editor.tracker.trackingSystem
                                      }),
                                      action.appInfo.osType
                                  )
                              }
                            : editor.tracker,
                        osType: action.appInfo ? String(action.appInfo.osType).toLowerCase() : undefined,
                        errors: []
                    }
                }
            };
        }
        case TrackingParametersEditorActionType.UPDATE_APP_TRACKERS_TEMPLATES: {
            return {
                ...state,
                trackersTemplates: action.trackersTemplates
            };
        }
        case TrackingParametersEditorActionType.UPDATE_APP_TRACKERS_ERRORS: {
            const editor = state.data[action.editorId];

            return {
                ...state,
                data: {
                    ...state.data,
                    [action.editorId]: {
                        ...editor,
                        errors: action.errors
                    }
                }
            };
        }
        case TrackingParametersEditorActionType.TRACKING_SYSTEM_CHANGE: {
            const editor = state.data[action.editorId] as TrackingParametersEditorState;
            const newTracker = assign({}, editor.tracker, action.tracker);
            const newTrackerUrl = trackUrlBuild(
                newTracker,
                find(state.trackersTemplates, {
                    trackingSystem: newTracker.trackingSystem
                }),
                editor.osType
            );
            const oldSelectedUserParams = get(editor, 'appSettings.tracker.selectedUserParams', []);
            const newSelectedUserParams = get(newTracker, 'selectedUserParams', []);

            const errors = editor.errors
                ? editor.errors.filter(({ path }) => 'trackers[0].url' !== path && 'trackers[0].trackerId' !== path)
                : [];
            const newErrors: Array<ErrorItem> = [];

            if (oldSelectedUserParams.length && errors.length) {
                errors.forEach(error => {
                    if (error.path.indexOf('userParams') !== 1) {
                        const oldParamIndex = chain(error.path)
                            .toPath()
                            .chunk(2)
                            .fromPairs()
                            .get('userParams')
                            .value();

                        const paramName = get(oldSelectedUserParams, `[${oldParamIndex}].name`);

                        if (paramName) {
                            const newParamIndex = findIndex(newSelectedUserParams, { name: paramName });

                            if (newParamIndex !== -1) {
                                const newPath = `trackers[0].userParams[${newParamIndex}]`;

                                newErrors.push(merge(error, { path: newPath }));
                            }
                        }
                    } else {
                        newErrors.push(error);
                    }
                });
            }

            return {
                ...state,
                data: {
                    ...state.data,
                    [action.editorId]: {
                        ...editor,
                        tracker: {
                            ...newTracker,
                            ...newTrackerUrl
                        },
                        errors: newErrors
                    }
                }
            };
        }
        case TrackingParametersEditorActionType.UPDATE_APP_TRACKERS_TRACKER_DATA: {
            const actionTracker = action.tracker;
            const tracker = trackingUrlParse(
                actionTracker,
                find(state.trackersTemplates, {
                    trackingSystem: actionTracker.trackingSystem
                })
            );
            const editor = state.data[action.editorId];

            return {
                ...state,
                data: {
                    ...state.data,
                    [action.editorId]: {
                        ...editor,
                        tracker: {
                            ...actionTracker,
                            ...tracker
                        },
                        errors: []
                    }
                }
            };
        }
        case TrackingParametersEditorActionType.SET_APP_TRACKERS_MOBILE_OS_TYPE: {
            const editor = state.data[action.editorId];

            return {
                ...state,
                data: {
                    ...state.data,
                    [action.editorId]: {
                        ...editor,
                        osType: String(action.osType).toLowerCase()
                    }
                }
            };
        }
        default:
            return state;
    }
}
